import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { firebaseAuth, firebaseConfigured } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { LanguageSelect } from '@/components/LanguageSelect';
import { authT } from '@/i18n/messages/auth';

/// Página que completa o fluxo de magic link (passwordless sign-in).
/// O backend (`/api/admin/users/invite`) gera um link via Firebase Admin SDK
/// que aponta pra `${origin}/auth/email-link?email=<email>`. Quando o usuário
/// clica, cai aqui — esta página detecta o oobCode no URL, chama
/// `signInWithEmailLink` e redireciona pra `/events`.
///
/// Pré-req obrigatório do Firebase (sem isso o link "morre" silenciosamente):
///  - Authentication → Sign-in method → Email/Password → "Email link
///    (passwordless sign-in)" habilitado
///  - Authentication → Settings → Authorized domains → events.faromaps.app
///    presente
///
/// Email no query string: o Firebase exige o email do usuário no client por
/// segurança (anti-phishing). Como o admin envia o link pra outra pessoa em
/// outro device, não dá pra pegar de localStorage. A solução é o backend
/// embutir `?email=...` no continueUrl. Se faltar (ex: link recebido sem o
/// param), página pede manualmente.
type State =
  | { kind: 'idle' }
  | { kind: 'verifying' }
  | { kind: 'needs-email' }
  | { kind: 'success' }
  | { kind: 'error'; message: string; code?: string };

export function AuthEmailLinkPage() {
  const t = authT().emailLink;
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [email, setEmail] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('email') ?? '';
  });

  useEffect(() => {
    if (!firebaseConfigured || !firebaseAuth) {
      setState({ kind: 'error', message: t.errors.notConfigured });
      return;
    }
    if (!isSignInWithEmailLink(firebaseAuth, window.location.href)) {
      setState({ kind: 'error', message: t.errors.invalidLink });
      return;
    }
    const initialEmail = email.trim();
    if (!initialEmail) {
      setState({ kind: 'needs-email' });
      return;
    }
    complete(initialEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function complete(targetEmail: string) {
    if (!firebaseAuth) return;
    setState({ kind: 'verifying' });
    try {
      await signInWithEmailLink(firebaseAuth, targetEmail, window.location.href);
      setState({ kind: 'success' });
      // Curta pausa pra usuário ver o checkmark antes do redirect. onIdTokenChanged
      // do AuthProvider já populou o store nesse meio tempo, então /events
      // não rebota pra /login.
      setTimeout(() => navigate('/events', { replace: true }), 600);
    } catch (err) {
      const code =
        err && typeof err === 'object' && 'code' in err
          ? String((err as { code: unknown }).code)
          : undefined;
      setState({
        kind: 'error',
        code,
        message: friendlyMessage(code, err),
      });
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-xl font-semibold">{t.title}</h1>
          <p className="text-sm text-muted-foreground">{t.confirming}</p>
        </div>

        {state.kind === 'idle' || state.kind === 'verifying' ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <Spinner className="h-6 w-6" />
            <p className="text-sm text-muted-foreground">{t.validating}</p>
          </div>
        ) : null}

        {state.kind === 'success' ? (
          <div className="text-center py-6 space-y-2">
            <p className="text-base">{t.success}</p>
          </div>
        ) : null}

        {state.kind === 'needs-email' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = email.trim();
              if (trimmed) complete(trimmed);
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="auth-email">{t.confirmEmailLabel}</Label>
              <Input
                id="auth-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.emailPlaceholder}
                className="min-h-[44px]"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">{t.emailWhy}</p>
            </div>
            <Button
              type="submit"
              disabled={!email.includes('@')}
              className="w-full min-h-[44px]"
            >
              {t.signIn}
            </Button>
          </form>
        ) : null}

        {state.kind === 'error' ? (
          <div className="space-y-3">
            <div className="rounded border border-destructive/40 bg-destructive/10 p-4 text-sm">
              <p className="font-medium">{t.failedTitle}</p>
              <p className="text-muted-foreground mt-1">{state.message}</p>
              {state.code ? (
                <p className="text-xs font-mono text-muted-foreground mt-2">
                  {state.code}
                </p>
              ) : null}
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/login', { replace: true })}
              className="w-full min-h-[44px]"
            >
              {t.goToLogin}
            </Button>
          </div>
        ) : null}

        {/* Só onde trocar de idioma é seguro: o recarregamento repete a
            URL, e com o código do link já em uso (verificando ou acabou de
            entrar) ele voltaria como "link já usado". Pedindo o email, o
            código ainda não foi gasto; no erro, não há o que perder. */}
        {state.kind === 'needs-email' || state.kind === 'error' ? (
          <LanguageSelect className="justify-center text-muted-foreground" />
        ) : null}
      </div>
    </div>
  );
}

function friendlyMessage(code: string | undefined, fallback: unknown): string {
  const t = authT().emailLink.errors;
  switch (code) {
    case 'auth/invalid-action-code':
    case 'auth/expired-action-code':
      return t.expiredOrUsed;
    case 'auth/invalid-email':
      return t.invalidEmail;
    case 'auth/user-disabled':
      return t.userDisabled;
    case 'auth/network-request-failed':
      return t.network;
    default:
      if (fallback instanceof Error) return fallback.message;
      return t.unexpected;
  }
}
